// Chakra imports
import { Badge, Box, Flex, IconButton, SimpleGrid, Text } from '@chakra-ui/react';

import TableComponent from 'components/TableComponent';
import { queryClient } from 'index';
import { DateTime } from 'luxon';
import { useMemo } from 'react';
import { FaEye } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import PullToRefresh from 'react-simple-pull-to-refresh';
import { Column } from 'react-table';
import { ChargebackDTO, findMyChargebacks } from 'services/api.service';
import { maskCurrency } from 'utils/number';

type ChargebackStatus = ChargebackDTO['status'];

const CHARGEBACK_STATUS_LABEL: Record<ChargebackStatus, string> = {
  OPEN: 'Em aberto',
  IN_DISPUTE: 'Em disputa',
  ACCEPTED: 'Acatado',
  REVERSED: 'Revertido',
  CANCELED: 'Cancelado',
};

const CHARGEBACK_STATUS_SCHEME: Record<ChargebackStatus, string> = {
  OPEN: 'orange',
  IN_DISPUTE: 'yellow',
  ACCEPTED: 'red',
  REVERSED: 'green',
  CANCELED: 'gray',
};

export const ChargebackStatusRender = (status: ChargebackStatus) => (
  <Badge colorScheme={CHARGEBACK_STATUS_SCHEME[status] ?? 'gray'}>
    {CHARGEBACK_STATUS_LABEL[status] ?? status}
  </Badge>
);

export default function Chargebacks() {
  const navigate = useNavigate();

  const columns = useMemo(
    () =>
      [
        {
          Header: 'Venda',
          accessor: 'movement.transactionCode',
          Cell: ({ row }: any) => (
            <Text noOfLines={1} fontSize="sm">
              {row.original.movement?.transactionCode ?? row.original.movementId}
            </Text>
          ),
        },
        {
          Header: 'Valor bloqueado',
          accessor: 'blockedAmount',
          align: 'right',
          Cell: ({ value }: { value: string }) => {
            return maskCurrency(Number(value));
          },
        },
        {
          Header: 'Motivo',
          accessor: 'reason',
          Cell: ({ value }: { value: string }) => (
            <Text noOfLines={1} fontSize="sm">
              {value}
            </Text>
          ),
        },
        {
          Header: 'Data',
          accessor: 'chargebackDate',
          Cell: ({ value, row }: any) => {
            const iso = value ?? row.original.createdAt;
            return iso ? DateTime.fromISO(iso).toFormat('dd/MM/yyyy HH:mm:ss') : '---';
          },
        },
        {
          Header: 'Status',
          accessor: 'status',
          filter: true,
          values: [
            { value: 'OPEN', label: 'Em aberto' },
            { value: 'IN_DISPUTE', label: 'Em disputa' },
            { value: 'ACCEPTED', label: 'Acatado' },
            { value: 'REVERSED', label: 'Revertido' },
            { value: 'CANCELED', label: 'Cancelado' },
          ],
          Cell: ({ value }: { value: ChargebackStatus }) => {
            return ChargebackStatusRender(value);
          },
        },
        {
          Header: 'Ações',
          accessor: 'actions',
        },
      ] as Column[],
    [],
  );

  const actions = (row: ChargebackDTO) => (
    <Flex
      align="center"
      justify="flex-start"
      w="100%"
    >
      <IconButton
        aria-label="Ver venda"
        size="sm"
        variant="outline"
        icon={<FaEye />}
        onClick={() => navigate(`/access/movement/${row.movementId}`)}
      />
    </Flex>
  );

  const handleRefresh = async () => {
    await queryClient.invalidateQueries({ queryKey: ['findMyChargebacks'] });
  };

  return (
    <PullToRefresh onRefresh={handleRefresh}>
      <Box>
        <SimpleGrid
          mb="20px"
          columns={{ sm: 1 }}
          spacing={{ base: '20px', xl: '20px' }}
        >
          <TableComponent
            columnsData={columns}
            queryFn={findMyChargebacks}
            queryKey="findMyChargebacks"
            actions={actions}
            label="Meus chargebacks"
          />
        </SimpleGrid>
      </Box>
    </PullToRefresh>
  );
}
